import React, { useState, useEffect, useRef } from "react";
import "./AccessControlModal.css";
import Input from "../../../../components/reusable-input/Input";
import { EyeOffIcon, EyeOpenIcon } from "../../../../icons/reactIcons";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import { fetchRoleListThunk } from "../../../../redux/slices/accessControlSlice";

interface AccessControlModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  mode: string;
  initialData?: any;
}

const AccessControlModal: React.FC<AccessControlModalProps> = ({
  open,
  onClose,
  onSubmit,
  mode,
  initialData,
}) => {
  const dispatch = useAppDispatch();
  const { roles, actionLoading, roleLoading } = useAppSelector(
    (state) => state.admin,
  );

  const passwordRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    roleId: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Fetch roles
  useEffect(() => {
    if (open) {
      dispatch(fetchRoleListThunk());
    }
  }, [dispatch, open]);

  // Reset / Populate
  useEffect(() => {
    if (open) {
      if (initialData && mode === "edit") {
        setFormData({
          roleId: initialData.role?.id || "",
          name: initialData.name || "",
          email: initialData.email || "",
          password: "",
          confirmPassword: "",
        });
      } else {
        setFormData({
          roleId: "",
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
      }

      setConfirmError("");
      setPasswordError("");
      setShowPasswordTooltip(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [initialData, open, mode]);

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        passwordRef.current &&
        !passwordRef.current.contains(event.target as Node)
      ) {
        setShowPasswordTooltip(false);
      }
    };

    if (showPasswordTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPasswordTooltip]);

  if (!open) return null;

  // Password rules
  const passwordRules = {
    minLength: formData.password.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.password),
    hasLowerCase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      setShowPasswordTooltip(true);
      setPasswordError("");

      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setConfirmError("Passwords do not match");
      } else {
        setConfirmError("");
      }
    }

    if (name === "confirmPassword") {
      if (value !== formData.password) {
        setConfirmError("Passwords do not match");
      } else {
        setConfirmError("");
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === "add" || mode === "reset") {
      if (!isPasswordValid) {
        setPasswordError("Password must meet all the above requirements.");
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setConfirmError("Passwords do not match");
        return;
      }
    }

    onSubmit(formData);
  };

  return (
    <div className="access-modal-overlay">
      <div className="access-modal">
        <h2>{mode === "edit" ? "Edit Access" : "Create New Access"}</h2>

        <form onSubmit={handleSubmit}>
          <div className="access-modal-body">
            {mode !== "reset" && (
              <>
                <div className="form-group">
                  <label>
                    Role <span className="required">*</span>
                  </label>
                  <select
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleChange}
                    required
                    disabled={roleLoading}
                  >
                    <option value="" disabled>
                      Select Role
                    </option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email ID <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={mode === "edit"}
                  />
                </div>
              </>
            )}

            {(mode === "add" || mode === "reset") && (
              <>
                {/* PASSWORD */}
                <div className="form-group password-wrapper" ref={passwordRef}>
                  <Input
                    name="password"
                    type="password"
                    placeholder="Enter Password"
                    label="Enter Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword((prev) => !prev)}
                    endIcon={showPassword ? <EyeOpenIcon /> : <EyeOffIcon />}
                    copy
                  />

                  {passwordError && (
                    <p className="error-text">{passwordError}</p>
                  )}

                  {showPasswordTooltip && (
                    <div className="password-tooltip">
                      <p className={passwordRules.minLength ? "valid" : ""}>
                        • At least 8 characters
                      </p>
                      <p className={passwordRules.hasUpperCase ? "valid" : ""}>
                        • One uppercase letter
                      </p>
                      <p className={passwordRules.hasLowerCase ? "valid" : ""}>
                        • One lowercase letter
                      </p>
                      <p className={passwordRules.hasNumber ? "valid" : ""}>
                        • One number
                      </p>
                      <p
                        className={passwordRules.hasSpecialChar ? "valid" : ""}
                      >
                        • One special character
                      </p>
                    </div>
                  )}
                </div>

                {/* CONFIRM PASSWORD */}
                <div className="form-group">
                  <Input
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    label="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    showPassword={showConfirmPassword}
                    onTogglePassword={() =>
                      setShowConfirmPassword((prev) => !prev)
                    }
                    endIcon={
                      showConfirmPassword ? <EyeOpenIcon /> : <EyeOffIcon />
                    }
                  />

                  {confirmError && <p className="error-text">{confirmError}</p>}
                </div>
              </>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>

            <button
              type="submit"
              className="submit-btn"
              disabled={actionLoading}
            >
              {actionLoading
                ? "Submitting..."
                : mode === "edit"
                  ? "Update"
                  : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccessControlModal;
